import { useEffect, useRef } from 'react'

/**
 * Keeps the selected row reachable: opens the branches holding it, then
 * scrolls to one when none is on screen.
 *
 * Both halves answer the same event, and a tree needs both — a row inside a
 * shut branch is not rendered at all, so there is nothing to scroll to until
 * `open` has run and the rows have rebuilt.
 *
 * A row is not always what was selected. The same configurator section is a
 * row under every category holding it, so the list can hold a dozen selected
 * rows at once, and while any of them is in view there is nothing to correct.
 * Only when none is does the list move, and then to the one nearest what the
 * reader was already looking at.
 *
 * `TableRow` marks a selected row with `data-selected`, which is what this
 * reads.
 *
 * `rowCount` re-runs it once the rows exist: a selection arriving from the
 * address is known before its rows are drawn, and finds nothing to scroll to.
 * Each selection is answered once, so opening a branch later never drags the
 * reader back — and shutting one the reader chose to shut stands.
 */
export function useRevealSelection(
  selection: string | null | undefined,
  rowCount: number,
  /**
   * Opens the branches holding `selection`, and reports whether it knew them.
   * Omitted by a flat list.
   */
  open?: (selection: string) => boolean,
) {
  const ref = useRef<HTMLDivElement>(null)
  const opened = useRef<string | null | undefined>(undefined)
  const revealed = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    if (selection == null || revealed.current === selection) return

    // Answered only once the branches are known: a selection arriving from
    // the address is read before the tree it names has loaded, and a latch set
    // on that first pass is one the loaded tree can never lift. A row that
    // never renders — one a search filters out — is why this latches on its
    // own rather than on the reveal below.
    if (open && opened.current !== selection && open(selection)) {
      opened.current = selection
    }

    const container = ref.current
    if (!container) return

    const rows = [...container.querySelectorAll<HTMLElement>('[data-selected]')]
    if (rows.length === 0) return

    revealed.current = selection

    const view = container.getBoundingClientRect()
    // Measured once: reading a rect inside the comparison forces a layout per
    // row, and the walk below reads every row again.
    const boxes = rows.map(row => ({ row, box: row.getBoundingClientRect() }))
    if (boxes.some(({ box }) => box.bottom > view.top && box.top < view.bottom))
      return

    const middle = (view.top + view.bottom) / 2
    const { row: nearest } = boxes.reduce((best, entry) =>
      Math.abs(entry.box.top - middle) < Math.abs(best.box.top - middle)
        ? entry
        : best,
    )

    // Centred, so the row lands where the eye already is rather than against an
    // edge with its neighbours cut off.
    nearest.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [selection, rowCount, open])

  return ref
}
